import { useCallback, useEffect, useRef, useState } from 'react'
import { getExchangeProvider } from '@/services/exchange/provider'
import type { ExchangeRate, ExchangeStatus } from '@/services/exchange/types'

const STALE_MS = 90_000

export interface UseExchangeRatesResult {
  rates: ExchangeRate[]
  fxRates: ExchangeRate[]
  goldRates: ExchangeRate[]
  status: ExchangeStatus
  error: string | null
  lastSuccessAt: Date | null
  isStale: boolean
  refresh: () => Promise<void>
}

function getInterval(): number {
  const raw = Number(import.meta.env.VITE_RATE_REFRESH_INTERVAL ?? 30_000)
  return Number.isFinite(raw) && raw >= 5_000 ? raw : 30_000
}

export function useExchangeRates(): UseExchangeRatesResult {
  const [rates, setRates] = useState<ExchangeRate[]>([])
  const [status, setStatus] = useState<ExchangeStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [lastSuccessAt, setLastSuccessAt] = useState<Date | null>(null)
  const [isStale, setIsStale] = useState(false)
  const hasData = useRef(false)

  const refresh = useCallback(async () => {
    setStatus((s) => (hasData.current ? s : 'loading'))
    try {
      const next = await getExchangeProvider().getRates()
      setRates(next)
      setError(null)
      setLastSuccessAt(new Date())
      setIsStale(false)
      setStatus('ready')
      hasData.current = true
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Kurlar alınamadı'
      setError(message)
      if (hasData.current) {
        setStatus('stale')
        setIsStale(true)
      } else {
        setStatus('error')
      }
    }
  }, [])

  useEffect(() => {
    void refresh()
    const interval = window.setInterval(() => {
      void refresh()
    }, getInterval())

    const staleTimer = window.setInterval(() => {
      setLastSuccessAt((prev) => {
        if (!prev) return prev
        if (Date.now() - prev.getTime() > STALE_MS) {
          setIsStale(true)
          setStatus((s) => (s === 'ready' ? 'stale' : s))
        }
        return prev
      })
    }, 5_000)

    return () => {
      window.clearInterval(interval)
      window.clearInterval(staleTimer)
    }
  }, [refresh])

  const fxRates = rates.filter((r) => r.category === 'fx')
  const goldRates = rates.filter((r) => r.category === 'gold')

  return {
    rates,
    fxRates,
    goldRates,
    status,
    error,
    lastSuccessAt,
    isStale,
    refresh,
  }
}
